import React from 'react';

import * as styles from './styles';
import Box from '@material-ui/core/Box';

import type {
  DateRange,
  UUID,
  Vehicle,
  VehicleGroup,
} from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import {
  CaptionDefault,
  Chip,
  InputLabel,
  type OptionKey,
  VehicleGroupFilter,
} from '@cobliteam/base-components';

import { MultiSelectVehicles } from '@cobliteam/ui/business';

import { TimeFilter } from '../../../TimeFilter/TimeFilter';

export interface FiltersSectionProps {
  vehicleGroups: VehicleGroup[];
  vehicles: Vehicle[];
  wantedGroups: VehicleGroup[];
  wantedVehicleIds: UUID[];
  wantedTimeSelection: DateRange;
  onVehicleGroupFilterChanged: (vehicleGroups: VehicleGroup[]) => void;
  onVehicleFilterChanged: (vehicleIds: UUID[]) => void;
  onTimeFilterChanged: (timeSelection: DateRange) => void;
}

enum FilterOption {
  GROUP = 'GROUP',
  VEHICLE = 'VEHICLE',
  TIME = 'TIME',
}

export const FiltersSection: React.FC<FiltersSectionProps> = ({
  vehicleGroups,
  vehicles,
  wantedGroups,
  wantedVehicleIds,
  wantedTimeSelection,
  ...props
}) => {
  const [filterOpened, setFilterOpened] = React.useState<
    FilterOption | undefined
  >(undefined);

  const getVehiclesCheckboxOptions = () => {
    const allVehicles = new Map<UUID, string>();

    vehicles.forEach((vehicle) => allVehicles.set(vehicle.id, vehicle.label));
    return Array.from(allVehicles.entries()).map(([vehicleId, label]) => ({
      key: vehicleId,
      label,
    }));
  };

  const handleGroupFilterApply = (selectedVehicleGroups: VehicleGroup[]) => {
    setFilterOpened(undefined);

    Analytics.track('Vehicle Group Filter Changed In Tracking History', {
      amount: selectedVehicleGroups.length,
    });
    props.onVehicleGroupFilterChanged(selectedVehicleGroups);
  };

  const handleVehicleFilterApply = (selectedVehicles: Vehicle[]) => {
    const vehicleIds = selectedVehicles.map((vehicle) => vehicle.id);
    setFilterOpened(undefined);
    props.onVehicleFilterChanged(vehicleIds);
  };

  const handleTimeFilterApply = (timeSelection: DateRange) => {
    setFilterOpened(undefined);
    props.onTimeFilterChanged(timeSelection);
  };

  const handleClearFilter = (filterType: string) => {
    Analytics.track(`Tracking History ${filterType} Filter Cleared`);
  };

  const handleGroupChipDelete = (deletedVehicleGroupId: UUID) => {
    const newWantedVehicleGroups = wantedGroups.filter(
      (group) => group.id !== deletedVehicleGroupId
    );
    props.onVehicleGroupFilterChanged(newWantedVehicleGroups);
  };

  const handleVehicleChipDelete = (deletedVehicleId: OptionKey) => {
    const newWantedVehicleIds = wantedVehicleIds.filter(
      (vehicleId) => vehicleId !== deletedVehicleId
    );
    props.onVehicleFilterChanged(newWantedVehicleIds);
  };

  const vehiclesOptions = getVehiclesCheckboxOptions();
  const wantedGroupIds = wantedGroups.map((group) => group.id);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        gridGap="50px"
        marginBottom="10px"
      >
        <Box display="flex" gridGap="20px" className={styles.filtersSection}>
          <VehicleGroupFilter
            vehicleGroups={vehicleGroups}
            appliedVehicleGroups={new Set(wantedGroupIds)}
            isOpen={filterOpened === FilterOption.GROUP}
            applyVehicleGroupsFilter={handleGroupFilterApply}
            onDropdownClick={() => setFilterOpened(FilterOption.GROUP)}
            onCancel={() => setFilterOpened(undefined)}
            onClear={() => handleClearFilter('Groups')}
          />

          <MultiSelectVehicles
            selectedVehicles={vehicles.filter((v) =>
              wantedVehicleIds.includes(v.id)
            )}
            onChange={handleVehicleFilterApply}
            onClear={() => handleClearFilter('Vehicles')}
          />
          <TimeFilter
            timeSelection={wantedTimeSelection}
            isOpen={filterOpened === FilterOption.TIME}
            onApply={handleTimeFilterApply}
            onClear={() => handleClearFilter('Hours')}
            onDropdownClick={() => setFilterOpened(FilterOption.TIME)}
            onCancel={() => setFilterOpened(undefined)}
          />
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gridGap="10px"
          flexWrap="wrap"
          className={styles.legend}
        >
          <CaptionDefault>Paradas</CaptionDefault>
          <CaptionDefault>Período de circulação do veículo</CaptionDefault>
          <CaptionDefault>Dispositivo desconectado</CaptionDefault>
          <CaptionDefault>Dispositivo conectado</CaptionDefault>
        </Box>
      </Box>
      {Boolean(wantedGroups.length || wantedVehicleIds.length) && (
        <div className={styles.chipsWrapper}>
          <InputLabel>Visualizando:</InputLabel>
          {wantedGroups.map((group, index) => (
            <Chip
              key={index}
              label={group.name}
              onDelete={() => handleGroupChipDelete(group.id)}
              disabled={!!wantedVehicleIds.length}
            />
          ))}
          {wantedVehicleIds.map((vehicleId, index) => {
            const chipOption = vehiclesOptions.find(
              ({ key }) => key === vehicleId
            );
            const chipLabel = chipOption && chipOption.label;
            return (
              <Chip
                key={index}
                label={chipLabel}
                onDelete={() => handleVehicleChipDelete(vehicleId)}
              />
            );
          })}
        </div>
      )}
    </>
  );
};
